import { useState, useEffect } from 'react';
import {
    Typography,
    Stack,
    TextField,
    Select,
    MenuItem,
    Button,
    Paper,
    Box,
    InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const cardSx = {
    bgcolor: '#fff',
    borderRadius: '15px',
    boxShadow: '1px 5px 5px 1px rgba(0,0,0,0.12)',
    p: { xs: 2, md: 2.5 },
};

const SearchMailing = ({
    theme,
    type: urlType = '',
    query: urlQuery = '',
    page = 1,
    loading = false,
    hasMore = false,
    onSearch,
    onPageChange,
    onFakeSearch,
}) => {
    const [query, setQuery] = useState(urlQuery);
    const [type, setType] = useState(urlType);
    const [error, setError] = useState("");

    useEffect(() => {
        setQuery(urlQuery);
        setType(urlType);
    }, [urlQuery, urlType]);

    const canSearch = Boolean(type && query.trim()) && !loading;
    const canPaginate = Boolean(type && query.trim()) && !loading;

    const handleSearch = () => {
        if (loading) return;

        if (!type || !query.trim()) {
            setError("Please enter a search term and select a search type.");
            return;
        }

        if (type === 'hs6Code' && /[a-zA-Z]/.test(query)) {
            setError("Commodity code must be numeric (e.g. 8501). Use Commodity Description for words.");
            return;
        }

        setError("");
        if (onSearch) {
            onSearch({ type, query: query.trim(), page: 1 });
        }
    };

    const handlePageChange = (newPage) => {
        if (loading || !type || !query.trim() || newPage < 1) return;
        if (onPageChange) {
            onPageChange(newPage);
        }
    };

    return (
        <Paper elevation={0} sx={cardSx}>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
                spacing={1.5}
                sx={{ mb: 2 }}
            >
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Company search
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Search by commodity, postcode, or company name.
                    </Typography>
                </Box>

                <Button
                    onClick={onFakeSearch}
                    theme={theme}
                    variant="outlined"
                    disabled={loading}
                    startIcon={<ScienceOutlinedIcon />}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '10px',
                    }}
                >
                    Load sample data
                </Button>
            </Stack>

            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'stretch', md: 'flex-start' }}
            >
                <TextField
                    label="Search"
                    placeholder="Commodity code, description, postcode, or company"
                    variant="outlined"
                    size="small"
                    sx={{
                        flex: 2,
                        '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                    }}
                    value={query}
                    disabled={loading}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            handleSearch();
                        }
                    }}
                    error={Boolean(error)}
                    helperText={error || ' '}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            </InputAdornment>
                        ),
                    }}
                />

                <Select
                    value={type}
                    onChange={(e) => {
                        setType(e.target.value);
                        if (error) setError('');
                    }}
                    size="small"
                    sx={{
                        flex: 1,
                        minWidth: { md: 200 },
                        borderRadius: '10px',
                    }}
                    theme={theme}
                    displayEmpty
                    disabled={loading}
                >
                    <MenuItem value="">
                        <em>Select search type</em>
                    </MenuItem>
                    <MenuItem value="hs6Code">Commodity Code</MenuItem>
                    <MenuItem value="hs6Desc">Commodity Description</MenuItem>
                    <MenuItem value="postcode">Trader by Postcode</MenuItem>
                    <MenuItem value="company">Trader by Name</MenuItem>
                </Select>

                <Button
                    variant="contained"
                    theme={theme}
                    onClick={handleSearch}
                    disabled={!canSearch}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '10px',
                        px: 3,
                        py: 1,
                        minWidth: 110,
                    }}
                >
                    Search
                </Button>
            </Stack>

            <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                sx={{
                    mt: 1,
                    pt: 2,
                    borderTop: '1px solid #eee',
                }}
            >
                <Button
                    variant="outlined"
                    disabled={!canPaginate || page === 1}
                    onClick={() => handlePageChange(page - 1)}
                    theme={theme}
                    startIcon={<ChevronLeftIcon />}
                    sx={{ textTransform: 'none', borderRadius: '10px' }}
                >
                    Previous
                </Button>

                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Page {page}
                </Typography>

                <Button
                    variant="outlined"
                    disabled={!canPaginate || !hasMore}
                    onClick={() => handlePageChange(page + 1)}
                    theme={theme}
                    endIcon={<ChevronRightIcon />}
                    sx={{ textTransform: 'none', borderRadius: '10px' }}
                >
                    Next
                </Button>
            </Stack>
        </Paper>
    );
};

export default SearchMailing;
